import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, ChevronUp, ListMusic, Loader2, Moon, Pause, Play, RotateCcw, RotateCw, SkipBack, SkipForward, Volume1, Volume2, VolumeX, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { PLAYER_RATES, SKIP_BACK_SECONDS, SKIP_FORWARD_SECONDS, usePlayer } from './PlayerProvider'
import { formatClock } from './ProgressBar'
import type { SleepTimerChoice } from './types'
import { usePlayerTone, type PlayerTone } from './tone'

export { PlayerToneContext, usePlayerTone, type PlayerTone } from './tone'

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0'
const toneIcon = (tone: PlayerTone, active?: boolean) =>
  tone === 'onColor'
    ? cn('text-white/85 hover:bg-[rgb(var(--pl-on-hero)/0.15)] hover:text-white focus-visible:ring-[rgb(var(--pl-on-hero))]', active && 'text-white')
    : cn('text-foreground-secondary hover:bg-muted hover:text-foreground focus-visible:ring-[rgb(var(--pl-accent))]', active && 'text-[rgb(var(--pl-accent-text))] hover:text-[rgb(var(--pl-accent-text))]')

/** The small dot under an active control (speed changed, timer on, list open), as Spotify marks it. */
const ActiveDot = ({ show }: { show?: boolean }) =>
  show ? <span aria-hidden className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current" /> : null

/** A round icon button of the player (44 px of touch area). */
export const PlayerIconButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean }>(
  ({ label, active, className, children, ...props }, ref) => {
    const tone = usePlayerTone()
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={cn('relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors disabled:pointer-events-none disabled:opacity-40', FOCUS, toneIcon(tone, active), className)}
        {...props}
      >
        {children}
        <ActiveDot show={active} />
      </button>
    )
  }
)
PlayerIconButton.displayName = 'PlayerIconButton'

/** −15 s / +30 s: the arrow with the seconds inside. */
export function SkipButton({ direction, disabled, className }: { direction: 'back' | 'forward'; disabled?: boolean; className?: string }) {
  const { t } = useTranslation('player')
  const { skip, current } = usePlayer()
  const seconds = direction === 'back' ? SKIP_BACK_SECONDS : SKIP_FORWARD_SECONDS
  const Icon = direction === 'back' ? RotateCcw : RotateCw
  return (
    <PlayerIconButton
      label={t(direction === 'back' ? 'back' : 'forward', { seconds })}
      disabled={disabled || !current}
      onClick={() => skip(direction === 'back' ? -seconds : seconds)}
      className={className}
    >
      <Icon className="h-7 w-7" strokeWidth={1.6} />
      <span aria-hidden className="absolute text-[9px] font-bold leading-none">{seconds}</span>
    </PlayerIconButton>
  )
}

const PLAY_SIZES = {
  sm: { box: 'h-9 w-9', icon: 'h-4 w-4' },
  md: { box: 'h-11 w-11', icon: 'h-5 w-5' },
  lg: { box: 'h-14 w-14', icon: 'h-6 w-6' },
  xl: { box: 'h-16 w-16', icon: 'h-7 w-7' }
} as const

/** Play/pause: the filled circle, in the cover's accent (white on a colored surface). */
export function PlayPauseButton({ size = 'md', onPlay, disabled, playing: playingOverride, label, className }: {
  size?: keyof typeof PLAY_SIZES
  onPlay?: () => void
  disabled?: boolean
  /** For a card whose episode is loaded: its own playing state. */
  playing?: boolean
  label?: string
  className?: string
}) {
  const { t } = useTranslation('player')
  const { status, toggle, current } = usePlayer()
  const tone = usePlayerTone()
  /* With `onPlay` the button stands for an episode that is not the one loaded: always "play". */
  const playing = playingOverride ?? (!onPlay && status === 'playing')
  const loading = !onPlay && playingOverride === undefined && status === 'loading'
  const { box, icon } = PLAY_SIZES[size]
  const text = label || (playing ? t('pause') : t('play'))
  return (
    <button
      type="button"
      aria-label={text}
      title={text}
      disabled={disabled || (!current && !onPlay)}
      onClick={() => (onPlay ? onPlay() : toggle())}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full shadow-md transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 motion-reduce:transition-none motion-reduce:hover:scale-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        tone === 'onColor'
          ? 'bg-[rgb(var(--pl-on-hero))] text-[rgb(var(--pl-hero-deep))] focus-visible:ring-[rgb(var(--pl-on-hero))] focus-visible:ring-offset-[rgb(var(--pl-hero))]'
          : 'bg-[rgb(var(--pl-accent))] text-[rgb(var(--pl-on-accent))] focus-visible:ring-[rgb(var(--pl-accent))] focus-visible:ring-offset-surface',
        box,
        className
      )}
    >
      {loading ? <Loader2 className={cn(icon, 'animate-spin')} /> : playing ? <Pause className={cn(icon, 'fill-current')} /> : <Play className={cn(icon, 'ml-0.5 fill-current')} />}
    </button>
  )
}

export function NextButton({ className }: { className?: string }) {
  const { t } = useTranslation('player')
  const { next, queue, current } = usePlayer()
  const hasNext = queue.length > 0 || Boolean(current?.next)
  return (
    <PlayerIconButton label={t('next')} disabled={!hasNext} onClick={next} className={className}>
      <SkipForward className="h-5 w-5 fill-current" />
    </PlayerIconButton>
  )
}

/** Previous, as on Spotify: back to the start, and from the start to the show's previous episode. */
export function PreviousButton({ className }: { className?: string }) {
  const { t } = useTranslation('player')
  const { current, position, seek, playEpisode } = usePlayer()
  const previous = current?.standalone ? null : current?.previous
  const onClick = () => {
    if (position > 3 || !previous) seek(0)
    else void playEpisode({ id: previous.id })
  }
  return (
    <PlayerIconButton label={t('previous')} disabled={!current} onClick={onClick} className={className}>
      <SkipBack className="h-5 w-5 fill-current" />
    </PlayerIconButton>
  )
}

const rateLabel = (rate: number) => `${rate.toString().replace(/\.0+$/, '')}×`

export function SpeedMenu({ className }: { className?: string }) {
  const { t } = useTranslation('player')
  const { rate, setRate } = usePlayer()
  const tone = usePlayerTone()
  const changed = rate !== 1
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('speed', { rate: rateLabel(rate) })}
          title={t('speedTitle')}
          className={cn('relative inline-flex h-11 min-w-11 shrink-0 items-center justify-center rounded-full px-2 text-sm font-semibold tabular-nums transition-colors', FOCUS, toneIcon(tone, changed), className)}
        >
          {rateLabel(rate)}
          <ActiveDot show={changed} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="top">
        <DropdownMenuLabel>{t('speedTitle')}</DropdownMenuLabel>
        {PLAYER_RATES.map((value) => (
          <DropdownMenuItem key={value} onSelect={() => setRate(value)} className={cn('justify-between gap-4 tabular-nums', value === rate && 'font-semibold text-[rgb(var(--pl-accent-text))]')}>
            {rateLabel(value)}
            {value === rate && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Minutes left on the sleep timer, refreshed every 15 s. */
const useSleepMinutesLeft = (endsAt: number | null | undefined): number | null => {
  const [, setTick] = React.useState(0)
  React.useEffect(() => {
    if (!endsAt) return
    const timer = window.setInterval(() => setTick((n) => n + 1), 15_000)
    return () => window.clearInterval(timer)
  }, [endsAt])
  return endsAt ? Math.max(1, Math.ceil((endsAt - Date.now()) / 60_000)) : null
}

const SLEEP_CHOICES: SleepTimerChoice[] = [15, 30, 60, 'end']

export function SleepMenu({ className }: { className?: string }) {
  const { t } = useTranslation('player')
  const { sleep, setSleepTimer, current } = usePlayer()
  const tone = usePlayerTone()
  const minutesLeft = useSleepMinutesLeft(sleep?.endsAt)
  const label = !sleep ? t('sleep.title') : sleep.choice === 'end' ? t('sleep.activeEnd') : t('sleep.activeMinutes', { count: minutesLeft ?? 0 })
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          disabled={!current}
          className={cn('relative inline-flex h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded-full px-2 transition-colors disabled:opacity-40', FOCUS, toneIcon(tone, Boolean(sleep)), className)}
        >
          <Moon className={cn('h-5 w-5', sleep && 'fill-current')} />
          {sleep && <span className="text-xs font-semibold tabular-nums">{sleep.choice === 'end' ? t('sleep.endShort') : `${minutesLeft}′`}</span>}
          <ActiveDot show={Boolean(sleep)} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="top">
        <DropdownMenuLabel>{t('sleep.title')}</DropdownMenuLabel>
        {SLEEP_CHOICES.map((choice) => (
          <DropdownMenuItem key={choice} onSelect={() => setSleepTimer(choice)} className={cn('justify-between gap-4', sleep?.choice === choice && 'font-semibold text-[rgb(var(--pl-accent-text))]')}>
            {choice === 'end' ? t('sleep.end') : t('sleep.minutes', { count: choice })}
            {sleep?.choice === choice && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
        {sleep && (
          <DropdownMenuItem onSelect={() => setSleepTimer(null)} className="text-foreground-secondary">
            {t('sleep.off')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Volume: mute on the icon, the level on a slim slider (desktop; phones use their own buttons). */
export function VolumeControl({ className }: { className?: string }) {
  const { t } = useTranslation('player')
  const { volume, setVolume } = usePlayer()
  const lastRef = React.useRef(volume || 1)
  if (volume > 0) lastRef.current = volume
  const Icon = volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2
  const percent = Math.round(volume * 100)
  return (
    <div className={cn('group/vol flex items-center', className)}>
      <PlayerIconButton label={volume === 0 ? t('unmute') : t('mute')} onClick={() => setVolume(volume === 0 ? lastRef.current : 0)}>
        <Icon className="h-5 w-5" />
      </PlayerIconButton>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={percent}
        aria-label={t('volume')}
        aria-valuetext={`${percent}%`}
        onChange={(event) => setVolume(Number(event.target.value) / 100)}
        className="player-range h-11 w-24 cursor-pointer bg-transparent"
        style={{ ['--range-fill' as string]: `${percent}%` }}
      />
    </div>
  )
}

/** Three bars that bounce on the playing row (still under reduced motion, frozen while paused). */
export function Equalizer({ paused, className }: { paused?: boolean; className?: string }) {
  return (
    <span aria-hidden data-paused={paused ? '' : undefined} className={cn('player-eq inline-flex h-3.5 w-3.5 items-end justify-between', className)}>
      <span className="h-full w-[3px] rounded-sm bg-current" />
      <span className="h-full w-[3px] rounded-sm bg-current" />
      <span className="h-full w-[3px] rounded-sm bg-current" />
    </span>
  )
}

/** The queue ("A seguir"): what is playing, then the list to play, reorder and remove; the same in every device of the account. */
export function QueueList({ className }: { className?: string }) {
  const { t } = useTranslation('player')
  const { queue, playFromQueue, removeFromQueue, moveInQueue, current, status } = usePlayer()
  const following = current?.next && !queue.length ? current.next : null
  return (
    <div className={cn('space-y-3', className)}>
      {current && (
        <div>
          <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('miniPlayer')}</p>
          <div className="flex items-center gap-3 rounded-md bg-[rgb(var(--pl-accent)/0.1)] px-2 py-2">
            <Equalizer paused={status !== 'playing'} className="text-[rgb(var(--pl-accent-text))]" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[rgb(var(--pl-accent-text))]">{current.title}</p>
              {current.show?.title && <p className="truncate text-xs text-muted-foreground">{current.show.title}</p>}
            </div>
          </div>
        </div>
      )}
      <div>
        <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('queue.title')}</p>
        {!queue.length ? (
          <div className="px-2 py-1 text-sm text-foreground-secondary">
            <p>{t('queue.empty')}</p>
            {following && <p className="mt-2 text-muted-foreground">{t('queue.thenShow', { title: following.title })}</p>}
          </div>
        ) : (
          <ol className="max-h-[min(50vh,22rem)] space-y-0.5 overflow-y-auto" aria-label={t('queue.title')}>
            {queue.map((item, index) => (
              <li key={item.episodeId} className="group flex items-center gap-2 rounded-md px-1 py-1 hover:bg-surface-muted">
                <span className="w-6 shrink-0 text-center text-xs tabular-nums text-muted-foreground group-hover:hidden">{index + 1}</span>
                <Play aria-hidden className="hidden h-3.5 w-6 shrink-0 fill-current text-foreground group-hover:block" />
                <button
                  type="button"
                  className="min-w-0 flex-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-accent))]"
                  onClick={() => { void playFromQueue(item.episodeId) }}
                  title={t('queue.playNow')}
                >
                  <span className="block text-sm font-medium leading-snug text-foreground line-clamp-2">{item.episode.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {item.episode.showTitle}
                    {item.episode.durationSeconds ? ` · ${formatClock(item.episode.durationSeconds)}` : ''}
                  </span>
                </button>
                <div className="flex shrink-0 items-center opacity-100 sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100">
                  <PlayerIconButton label={t('queue.moveUp')} disabled={index === 0} onClick={() => { void moveInQueue(item.episodeId, index - 1) }} className="h-9 w-9">
                    <ChevronUp className="h-4 w-4" />
                  </PlayerIconButton>
                  <PlayerIconButton label={t('queue.moveDown')} disabled={index === queue.length - 1} onClick={() => { void moveInQueue(item.episodeId, index + 1) }} className="h-9 w-9">
                    <ChevronDown className="h-4 w-4" />
                  </PlayerIconButton>
                  <PlayerIconButton label={t('queue.remove')} onClick={() => { void removeFromQueue(item.episodeId) }} className="h-9 w-9">
                    <X className="h-4 w-4" />
                  </PlayerIconButton>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

/** The queue button with its count, opening the list in a popover. */
export function QueueButton({ className, side = 'top' }: { className?: string; side?: 'top' | 'bottom' }) {
  const { t } = useTranslation('player')
  const { queue, canSync } = usePlayer()
  const [open, setOpen] = React.useState(false)
  if (!canSync) return null
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PlayerIconButton label={t('queue.open', { count: queue.length })} active={open} className={className}>
          <ListMusic className="h-5 w-5" />
          {queue.length > 0 && (
            <span className="absolute right-0.5 top-0.5 min-w-4 rounded-full bg-[rgb(var(--pl-accent))] px-1 text-[10px] font-semibold leading-4 text-[rgb(var(--pl-on-accent))] tabular-nums">{queue.length}</span>
          )}
        </PlayerIconButton>
      </PopoverTrigger>
      <PopoverContent side={side} align="end" className="w-[min(24rem,calc(100vw-1.5rem))] p-2">
        <QueueList />
      </PopoverContent>
    </Popover>
  )
}
